import { useState } from "react";
import { X, Coins, ShoppingBag, Check } from "lucide-react";

const TABS = [
  { id: "feeds", label: "Feed & Hay" },
  { id: "supplements", label: "Supplements" },
  { id: "treats", label: "Treats" },
  { id: "owned", label: "Owned" },
];

// Compatibility for the current horse, mirroring backend feeding-trait logic.
export const compat = (horse, item) => {
  if (!horse) return "neutral";
  const t = horse.feeding_trait || "";
  const p = horse.personality || [];
  const conc = item.concentrate;
  if (item.category === "treats") return item.id === horse.favorite_treat ? "good" : item.id === horse.disliked_treat ? "bad" : "neutral";
  if (conc && t === "Grain sensitive") return "bad";
  if (conc && (t === "Easy keeper" || t === "Gains weight easily")) return "bad";
  if (item.id === "perf_grain" && t === "High-energy metabolism") return "bad";
  if (item.id === "weight_builder" && t === "Hard keeper") return "good";
  if (item.id === "senior_feed" && t === "Senior") return "good";
  if (item.id === "digestive" && t === "Sensitive stomach") return "good";
  if (item.id === "calming" && p.includes("Reactive")) return "good";
  if (item.id === "premium_hay" && t === "Hard keeper") return "good";
  return "neutral";
};
const COMPAT_UI = {
  good: { label: "Recommended", cls: "bg-green-100 text-green-700" },
  neutral: { label: "Neutral", cls: "bg-muted text-muted-foreground" },
  bad: { label: "Not ideal for this horse", cls: "bg-red-100 text-red-700" },
};

const effectText = (it) => {
  if (it.category === "treats") return "Mood +2 · Trust +1 (first treat/day only)";
  const e = Object.entries(it.eff || {}).map(([k, v]) => `${k} +${v}`).join(" · ");
  return e || "Small boost";
};

export const FeedRoom = ({ shop, inventory, currency, onBuy, onUse, onClose, horse, busy }) => {
  const [tab, setTab] = useState("feeds");
  const [confirm, setConfirm] = useState(null);

  const allItems = Object.entries(shop || {}).flatMap(([c, arr]) => arr.map((it) => ({ ...it, category: c })));
  const owned = allItems.filter((it) => (inventory?.[it.id] || 0) > 0);
  const list = tab === "owned" ? owned : (shop?.[tab] || []).map((it) => ({ ...it, category: tab }));

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center" data-testid="feed-room">
      <div className="flex max-h-[88vh] w-full max-w-md flex-col rounded-t-2xl border border-border bg-card shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="flex items-center gap-2 font-display text-xl font-bold text-primary"><ShoppingBag size={18} className="text-secondary" /> Feed Room</h3>
          <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-sm font-bold text-amber-700"><Coins size={14} /> {currency}</span>
          <button data-testid="feed-room-close" onClick={onClose} className="rounded-full p-1 text-muted-foreground hover:bg-muted"><X size={18} /></button>
        </div>

        <div className="flex gap-1 overflow-x-auto border-b border-border px-3 py-2">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`feed-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold ${tab === t.id ? "bg-secondary text-white" : "bg-muted text-muted-foreground"}`}>
              {t.label}{t.id === "owned" ? ` (${owned.length})` : ""}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto p-3">
          {tab === "feeds" && (
            <p className="mb-2 rounded-lg bg-green-50 px-3 py-2 text-xs font-semibold text-green-700">Grass hay &amp; fresh water are always free in Daily Care. These are optional extras.</p>
          )}
          {list.length === 0 && <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">Nothing here yet.</p>}
          <div className="space-y-2">
            {list.map((it) => {
              const c = COMPAT_UI[compat(horse, it)];
              const owns = inventory?.[it.id] || 0;
              return (
                <div key={it.id} data-testid={`shop-item-${it.id}`} className="rounded-xl border border-border bg-background p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-bold text-primary">{it.name}</p>
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${c.cls}`}>{c.label}</span>
                      <p className="mt-1 text-[11px] text-muted-foreground">{effectText(it)}</p>
                      {it.note && <p className="text-[11px] italic text-muted-foreground">{it.note}</p>}
                      <p className="mt-0.5 text-[11px] font-semibold text-foreground/70">{it.servings} servings/pack · Owned: {owns}</p>
                    </div>
                    <div className="flex shrink-0 flex-col gap-1.5">
                      <button data-testid={`buy-${it.id}`} disabled={busy || currency < it.price} onClick={() => setConfirm(it)}
                        className="flex items-center justify-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-40 active:scale-95">
                        <Coins size={12} /> {it.price}
                      </button>
                      {tab === "owned" && (
                        <button data-testid={`use-${it.id}`} disabled={busy} onClick={() => onUse(it.id, it.category)}
                          className="rounded-full border border-secondary px-3 py-1 text-xs font-bold text-secondary active:scale-95">Use Today</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/50 p-4" data-testid="buy-confirm">
          <div className="w-full max-w-xs rounded-2xl border border-border bg-card p-5 text-center shadow-xl">
            <p className="font-display text-lg font-bold text-primary">{confirm.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">Buy <span className="font-bold text-primary">{confirm.servings} servings</span> for <span className="font-bold text-amber-700">{confirm.price} coins</span>?</p>
            <p className="mt-1 text-[11px] text-muted-foreground">Adds to your inventory — you'll feed it in Daily Care.</p>
            <div className="mt-4 flex gap-2">
              <button data-testid="buy-cancel" onClick={() => setConfirm(null)} className="h-10 flex-1 rounded-xl border-2 border-border font-bold text-primary active:scale-95">Cancel</button>
              <button data-testid="buy-confirm-btn" disabled={busy} onClick={() => { onBuy(confirm.id); setConfirm(null); }}
                className="h-10 flex-1 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
